import React, { useEffect, useState } from 'react';
import { categoriesAPI, rulesAPI, historyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Sun, Moon, Monitor } from 'lucide-react';

interface Category {
  id: number;
  name: string;
  budget_priority?: string;
  color?: string;
}

interface Rule {
  id: number;
  pattern: string;
  category_id: number;
  category?: { name: string };
  is_regex: boolean;
  match_count: number;
}

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { mode, setMode } = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [rules, setRules] = useState<Rule[]>([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', budget_priority: '', color: '#6366f1' });
  const [ruleForm, setRuleForm] = useState({ pattern: '', category_id: '', is_regex: false });
  const [snapshotMessage, setSnapshotMessage] = useState('');

  const priorities = ['Critical', 'Important', 'Luxury'];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, ruleRes] = await Promise.all([
        categoriesAPI.getAll(),
        rulesAPI.getAll(),
      ]);
      // Sort categories alphabetically
      const sortedCategories = [...catRes.data].sort((a: Category, b: Category) =>
        a.name.localeCompare(b.name)
      );
      setCategories(sortedCategories);
      setRules(ruleRes.data);
    } catch (error) {
      console.error('Failed to fetch settings data:', error);
    }
  };

  const handleCreateCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await categoriesAPI.create(categoryForm);
      setShowCategoryModal(false);
      setCategoryForm({ name: '', budget_priority: '', color: '#6366f1' });
      fetchData();
    } catch (error) {
      console.error('Failed to create category:', error);
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm('Are you sure? This will uncategorize all related transactions.')) {
      try {
        await categoriesAPI.delete(id);
        fetchData();
      } catch (error) {
        console.error('Failed to delete category:', error);
      }
    }
  };

  const handleCreateRule = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await rulesAPI.create({
        ...ruleForm,
        category_id: parseInt(ruleForm.category_id),
      });
      setShowRuleModal(false);
      setRuleForm({ pattern: '', category_id: '', is_regex: false });
      fetchData();
    } catch (error) {
      console.error('Failed to create rule:', error);
    }
  };

  const handleDeleteRule = async (id: number) => {
    try {
      await rulesAPI.delete(id);
      fetchData();
    } catch (error) {
      console.error('Failed to delete rule:', error);
    }
  };

  const handleCreateSnapshot = async () => {
    try {
      await historyAPI.createSnapshot();
      setSnapshotMessage('Snapshot created successfully!');
      setTimeout(() => setSnapshotMessage(''), 3000);
    } catch (error) {
      console.error('Failed to create snapshot:', error);
      setSnapshotMessage('Failed to create snapshot');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      await categoriesAPI.seedDefaults();
      fetchData();
    } catch (error) {
      console.error('Failed to seed default categories:', error);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">Manage your account and preferences</p>
      </div>

      {/* Account Info */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Account</h3>
        </div>
        <div style={{ padding: '0.5rem 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Name</span>
            <span>{user?.name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Email</span>
            <span>{user?.email}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Authentication</span>
            <span>
              {user?.has_password && 'Password'}
              {user?.has_password && user?.has_google && ' + '}
              {user?.has_google && 'Google'}
            </span>
          </div>
        </div>
      </div>

      {/* Theme Settings */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Theme</h3>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            className={`btn ${mode === 'system' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMode('system')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Monitor size={18} />
            System
          </button>
          <button
            className={`btn ${mode === 'light' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMode('light')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Sun size={18} />
            Light
          </button>
          <button
            className={`btn ${mode === 'dark' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setMode('dark')}
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <Moon size={18} />
            Dark
          </button>
        </div>
      </div>

      {/* Net Worth Snapshot */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Net Worth Tracking</h3>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Create a snapshot of your current net worth to track progress over time.
        </p>
        <button className="btn btn-primary" onClick={handleCreateSnapshot}>
          Create Snapshot
        </button>
        {snapshotMessage && (
          <span style={{ marginLeft: '1rem', color: 'var(--success)' }}>{snapshotMessage}</span>
        )}
      </div>

      {/* Categories */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Transaction Categories</h3>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {categories.length === 0 && (
              <button className="btn btn-secondary btn-sm" onClick={handleSeedDefaults}>
                Load Defaults
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={() => setShowCategoryModal(true)}>
              + Add Category
            </button>
          </div>
        </div>
        {categories.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Budget Priority</th>
                  <th>Color</th>
                  <th style={{ width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat) => (
                  <tr key={cat.id}>
                    <td>{cat.name}</td>
                    <td>
                      {cat.budget_priority && (
                        <span className={`badge badge-${cat.budget_priority.toLowerCase()}`}>
                          {cat.budget_priority}
                        </span>
                      )}
                    </td>
                    <td>
                      <div
                        style={{
                          width: '24px',
                          height: '24px',
                          borderRadius: '4px',
                          backgroundColor: cat.color || '#6366f1',
                        }}
                      />
                    </td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteCategory(cat.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>No categories yet. Click "Load Defaults" for common categories or create your own.</p>
        )}
      </div>

      {/* Categorization Rules */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Auto-Categorization Rules</h3>
          <button className="btn btn-primary btn-sm" onClick={() => setShowRuleModal(true)}>
            + Add Rule
          </button>
        </div>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>
          Create rules to automatically categorize transactions based on their description.
        </p>
        {rules.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Pattern</th>
                  <th>Category</th>
                  <th>Type</th>
                  <th>Matches</th>
                  <th style={{ width: '80px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.id}>
                    <td><code>{rule.pattern}</code></td>
                    <td>{rule.category?.name || 'Unknown'}</td>
                    <td>{rule.is_regex ? 'Regex' : 'Contains'}</td>
                    <td>{rule.match_count}</td>
                    <td>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p style={{ color: 'var(--text-secondary)' }}>
            No rules yet. Rules help automatically categorize imported transactions.
          </p>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Category</h3>
              <button className="modal-close" onClick={() => setShowCategoryModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateCategory}>
              <div className="form-group">
                <label className="form-label">Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="e.g., Groceries, Utilities"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Budget Priority (optional)</label>
                <select
                  className="form-input"
                  value={categoryForm.budget_priority}
                  onChange={(e) => setCategoryForm({ ...categoryForm, budget_priority: e.target.value })}
                >
                  <option value="">No priority</option>
                  {priorities.map((p) => (
                    <option key={p} value={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Color</label>
                <input
                  type="color"
                  className="form-input"
                  value={categoryForm.color}
                  onChange={(e) => setCategoryForm({ ...categoryForm, color: e.target.value })}
                  style={{ height: '40px', padding: '4px' }}
                />
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowCategoryModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Category
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Categorization Rule</h3>
              <button className="modal-close" onClick={() => setShowRuleModal(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateRule}>
              <div className="form-group">
                <label className="form-label">Pattern</label>
                <input
                  type="text"
                  className="form-input"
                  value={ruleForm.pattern}
                  onChange={(e) => setRuleForm({ ...ruleForm, pattern: e.target.value })}
                  placeholder="e.g., UBER, AMAZON, NETFLIX"
                  required
                />
                <small style={{ color: 'var(--text-secondary)' }}>
                  Transactions containing this text will be auto-categorized
                </small>
              </div>
              <div className="form-group">
                <label className="form-label">Category</label>
                <select
                  className="form-input"
                  value={ruleForm.category_id}
                  onChange={(e) => setRuleForm({ ...ruleForm, category_id: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={ruleForm.is_regex}
                    onChange={(e) => setRuleForm({ ...ruleForm, is_regex: e.target.checked })}
                  />
                  <span>Use regex pattern (advanced)</span>
                </label>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowRuleModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Rule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
